import React, { useMemo } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';
import { FormTextField, SubmitButton } from 'components';
import { ZipCodeField } from 'components/ZipCodeField';
import { usePartnerUpdateRequestInsuranceInsert } from 'hooks/requests';
import { StateSelect } from 'components/StateSelect';
import { SexField } from 'components/SexField';
import { DOBField } from 'components/DOBField';
import { InsuredRelationshipToPatientField } from 'components/InsuredRelationshipToPatientField';
import { format } from 'date-fns/esm';
import { InsuranceInfo, PatientInsuranceInfo } from 'types/patientInsurance';
import { useAuthentication } from '../../../hooks/useAuthentication';
import { AlertColor, Box, Stack } from '@mui/material';

export interface InsuranceFormProps {
  ordinal?: string;
  data?: PatientInsuranceInfo;
  initialValues?: Partial<InsuranceInfo>;
  setSnackbarState: (state: {
    message?: string;
    open?: boolean;
    severity?: AlertColor;
  }) => void;
}

export const InsuranceForm: React.FC<InsuranceFormProps> = ({
  ordinal = 'primary',
  data = {},
  initialValues = {},
  setSnackbarState
}) => {
  const ids = {
    primary: 4,
    secondary: 5,
    tertiary: 6
  };
  const id = ids[ordinal as keyof typeof ids];
  const { mutateAsync: updateMutation } =
    usePartnerUpdateRequestInsuranceInsert();
  const { isLoggedInSSO } = useAuthentication();

  async function _handleSubmit(
    values: typeof initValues,
    actions: FormikHelpers<typeof initValues>
  ) {
    try {
      await updateMutation({
        ...data,
        reqAction: id,
        [`${ordinal}Insurance`]: {
          ...values,
          insuredDOB: values?.insuredDOB ? new Date(values.insuredDOB) : ''
        }
      } as unknown as PatientInsuranceInfo);

      setSnackbarState({
        message: 'Update request saved successfully',
        severity: 'success',
        open: true
      });
    } catch (err) {
      setSnackbarState({
        message: (err as { Error: string }).Error || 'An Error has occured.',
        severity: 'error',
        open: true
      });
    }
    actions.setSubmitting(false);
  }

  const initValues = useMemo(() => {
    return {
      id: data.id,
      planName: '',
      groupID: '',
      policyID: '',
      companyAddress: '',
      companyCity: '',
      companyState: '',
      companyZip: '',
      insuredRelationshipToPatient: '',
      insuredFirstName: '',
      insuredLastName: '',
      insuredSex: '',
      insuredAddress: '',
      insuredCity: '',
      insuredState: '',
      insuredZip: '',
      employerName: '',
      ...initialValues,
      insuredDOB: initialValues.insuredDOB
        ? format(new Date(initialValues.insuredDOB), 'MM/dd/yyyy')
        : ''
    };
  }, [data, initialValues]);

  return (
    <Formik<typeof initValues>
      enableReinitialize
      initialValues={initValues}
      onSubmit={_handleSubmit}
    >
      {({ isSubmitting, isValidating }) => {
        const isFormSubmitting = isSubmitting || isValidating;

        return (
          <Form style={{ padding: '0 24px 20px 24px' }}>
            <Stack>
              <Box
                sx={{
                  display: 'grid',
                  columnGap: '12px',
                  rowGap: '14px',
                  gridTemplateColumns: 'auto auto auto auto'
                }}
              >
                <FormTextField
                  name="planName"
                  isRequired={false}
                  label="Plan Name"
                />
                <FormTextField
                  name="groupID"
                  isRequired={false}
                  label="Group ID"
                />
                <FormTextField
                  name="policyID"
                  isRequired={false}
                  label="Policy ID"
                />
                <FormTextField
                  name="companyAddress"
                  isRequired={false}
                  label="Insurance Address"
                />
                <FormTextField
                  name="companyCity"
                  isRequired={false}
                  label="Insurance City"
                />
                <StateSelect
                  name="companyState"
                  isRequired={false}
                  label="Insurance State"
                />
                <ZipCodeField
                  name="companyZip"
                  isRequired={false}
                  label="Insurance Zip"
                />
                <InsuredRelationshipToPatientField name="insuredRelationshipToPatient" />

                <FormTextField
                  name="insuredFirstName"
                  isRequired
                  alphaOnly
                  label="Insured First Name"
                />
                <FormTextField
                  name="insuredLastName"
                  isRequired
                  alphaOnly
                  label="Insured Last Name"
                />
                <SexField
                  name="insuredSex"
                  isRequired={false}
                  label="Insured Sex"
                />
                <FormTextField
                  name="insuredAddress"
                  isRequired={false}
                  label="Insured Address"
                />
                <FormTextField
                  name="insuredCity"
                  isRequired={false}
                  label="Insured City"
                />
                <StateSelect
                  name="insuredState"
                  isRequired={false}
                  label="Insured State"
                />
                <ZipCodeField
                  name="insuredZip"
                  isRequired={false}
                  label="Insured Zip"
                />
                <DOBField name="insuredDOB" isRequired label="Insured DOB" />
                <FormTextField
                  name="employerName"
                  isRequired={false}
                  label="Employer Name"
                />
              </Box>

              {isLoggedInSSO() ? null : (
                <div style={{ marginLeft: 'auto' }}>
                  <SubmitButton
                    disabled={isFormSubmitting}
                    loading={isFormSubmitting}
                  >
                    Submit Update Request
                  </SubmitButton>
                </div>
              )}
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};
